import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createNewPolicy,
  deletePolicy,
  editPolicy,
  getAllPolicies,
  getPolicyById,
  getPoliciesByType,
  createPolicyMultilingual,
  editPolicyMultilingual,
  getPolicyByIdMultilingual,
} from './thunks';
import { PoliciesState } from './types';

export const name = 'policies';

const initialState: PoliciesState = {
  data: [],
  details: null,
  detailsHistory: [],
  status: 'idle',
  updatingStatus: 'idle',
  policiesByType: {},
  createMultilingual: {
    data: null,
    status: 'idle',
  },
  editMultilingual: {
    data: null,
    status: 'idle',
  },
  byIdMultilingual: {
    data: null,
    status: 'idle',
  },
};

export const getAllPoliciesAsync = createAsyncThunk(
  name + '/getAllPolicies',
  getAllPolicies
);
export const getPoliciesByTypeAsync = createAsyncThunk(
  name + '/getPoliciesByType',
  getPoliciesByType
);
export const getPolicyByIdAsync = createAsyncThunk(
  name + '/getPolicyById',
  getPolicyById
);
export const createNewPolicyAsync = createAsyncThunk(
  name + '/createNewPolicy',
  createNewPolicy
);
export const createPolicyMultilingualAsync = createAsyncThunk(
  name + '/createPolicyMultilingual',
  createPolicyMultilingual
);
export const editPolicyMultilingualAsync = createAsyncThunk(
  name + '/editPolicyMultilingual',
  editPolicyMultilingual
);
export const getPolicyByIdMultilingualAsync = createAsyncThunk(
  name + '/getPolicyByIdMultilingual',
  getPolicyByIdMultilingual
);
export const editPolicyAsync = createAsyncThunk(
  name + '/editPolicy',
  editPolicy
);
export const deletePolicyAsync = createAsyncThunk(
  name + '/deletePolicy',
  deletePolicy
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearDetails: (state) => {
      state.details = null;
    },
    clearByIdMultilingual: (state) => {
      state.byIdMultilingual = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPoliciesAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAllPoliciesAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.data = action.payload;
      })
      .addCase(getAllPoliciesAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getPoliciesByTypeAsync.pending, (state, action) => {
        state.policiesByType = {
          ...state.policiesByType,
          [action.meta.arg.type]: { status: 'loading' },
        };
      })
      .addCase(getPoliciesByTypeAsync.fulfilled, (state, action) => {
        state.policiesByType = {
          ...state.policiesByType,
          [action.meta.arg.type]: { data: action.payload, status: 'idle' },
        };
      })
      .addCase(getPoliciesByTypeAsync.rejected, (state, action) => {
        state.policiesByType = {
          ...state.policiesByType,
          [action.meta.arg.type]: { status: 'failed' },
        };
      })
      .addCase(getPolicyByIdAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPolicyByIdAsync.fulfilled, (state, action) => {
        return {
          ...state,
          status: 'idle',
          details: action.payload,
          detailsHistory: [...state.detailsHistory, action.payload],
        };
      })
      .addCase(getPolicyByIdAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(createNewPolicyAsync.pending, (state) => {
        state.updatingStatus = 'loading';
      })
      .addCase(createNewPolicyAsync.fulfilled, (state) => {
        state.updatingStatus = 'idle';
      })
      .addCase(createNewPolicyAsync.rejected, (state) => {
        state.updatingStatus = 'failed';
      })
      .addCase(editPolicyAsync.pending, (state) => {
        state.updatingStatus = 'loading';
      })
      .addCase(editPolicyAsync.fulfilled, (state) => {
        state.updatingStatus = 'idle';
      })
      .addCase(editPolicyAsync.rejected, (state) => {
        state.updatingStatus = 'failed';
      })
      .addCase(createPolicyMultilingualAsync.pending, (state) => {
        state.createMultilingual.status = 'loading';
      })
      .addCase(createPolicyMultilingualAsync.fulfilled, (state, action) => {
        state.createMultilingual = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(createPolicyMultilingualAsync.rejected, (state) => {
        state.createMultilingual.status = 'failed';
      })
      .addCase(editPolicyMultilingualAsync.pending, (state) => {
        state.editMultilingual.status = 'loading';
      })
      .addCase(editPolicyMultilingualAsync.fulfilled, (state, action) => {
        state.editMultilingual = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(editPolicyMultilingualAsync.rejected, (state) => {
        state.editMultilingual.status = 'failed';
      })
      .addCase(getPolicyByIdMultilingualAsync.pending, (state) => {
        state.byIdMultilingual.status = 'loading';
      })
      .addCase(getPolicyByIdMultilingualAsync.fulfilled, (state, action) => {
        state.byIdMultilingual = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getPolicyByIdMultilingualAsync.rejected, (state) => {
        state.byIdMultilingual.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
